import { useState, useRef, useCallback, useEffect } from 'react';
import {
    handleMultimodalGenerate,
    handleMultimodalGenerateStatus,
    handleMultimodalGenerateResult
} from '@/api/grt/ai';
import ToastUtils from '@/utils/ToastUtils';
import useMultimodalReference from './useMultimodalReference';

/**
 * 多模态卡片生成管理 Hook
 * 负责管理卡片生成状态、轮询、结果获取等逻辑
 */
const useMultimodalCardGeneration = ({
    messageId,
    reference_id,
    task_list = [],
    detailPanelOpen,
    activeDetailMessageId,
    onOpenDetailModal
}) => {
    const {
        isSuccessStatus,
        isFailedStatus,
        extractStatus,
        getTaskType,
        initializeCardStatusFromTaskList,
        buildDisplayData
    } = useMultimodalReference();

    // 卡片生成状态按 messageId 区分
    const [cardStatusMap, setCardStatusMap] = useState({});
    const [generatedResultsMap, setGeneratedResultsMap] = useState({});
    const requestedTasksRef = useRef({});
    const lastStatusRef = useRef({});
    // 获取有效消息ID
    const getEffectiveMessageId = useCallback(() => {
        if (detailPanelOpen && activeDetailMessageId && activeDetailMessageId === messageId) {
            return activeDetailMessageId;
        }
        return messageId;
    }, [detailPanelOpen, activeDetailMessageId, messageId]);

    // 获取当前消息的状态
    const getCurrentCardStatus = useCallback(() => {
        const effectiveId = getEffectiveMessageId();
        if (!effectiveId) {
            return { text_summary: 'idle', mind_map: 'idle', video_player: 'idle' };
        }
        return (
            cardStatusMap[effectiveId] ||
            lastStatusRef.current[effectiveId] || {
                text_summary: 'idle',
                mind_map: 'idle',
                video_player: 'idle'
            }
        );
    }, [getEffectiveMessageId, cardStatusMap]);

    // 获取当前消息的结果
    const getCurrentGeneratedResults = useCallback(() => {
        const effectiveId = getEffectiveMessageId();
        if (!effectiveId) {
            return { text_summary: null, mind_map: null, video_player: null };
        }
        return generatedResultsMap[effectiveId] || {
            text_summary: null,
            mind_map: null,
            video_player: null
        };
    }, [getEffectiveMessageId, generatedResultsMap]);

    // 设置卡片状态
    const setCardStatusForId = useCallback((effectiveId, updater) => {
        if (!effectiveId) return;
        setCardStatusMap((prev) => {
            const nextValue =
                typeof updater === 'function'
                    ? updater(prev[effectiveId] || { text_summary: 'idle', mind_map: 'idle', video_player: 'idle' })
                    : updater;
            const merged = { ...prev, [effectiveId]: nextValue };
            lastStatusRef.current[effectiveId] = nextValue;
            return merged;
        });
    }, []);

    const setCurrentCardStatus = useCallback((updater) => {
        const effectiveId = getEffectiveMessageId();
        setCardStatusForId(effectiveId, updater);
    }, [getEffectiveMessageId, setCardStatusForId]);

    const setCurrentGeneratedResults = useCallback((updater) => {
        const effectiveId = getEffectiveMessageId();
        if (!effectiveId) return;
        setGeneratedResultsMap(prev => ({
            ...prev,
            [effectiveId]: typeof updater === 'function'
                ? updater(prev[effectiveId] || { text_summary: null, mind_map: null, video_player: null })
                : updater
        }));
    }, [getEffectiveMessageId]);

    // 获取生成结果
    const fetchGenerateResult = useCallback(async (taskId, taskType, cardType, ele) => {
        try {
            const resultResponse = await handleMultimodalGenerateResult({
                task_id: taskId,
                task_type: taskType
            });
            const resultData = resultResponse?.data || resultResponse;
            const displayData = buildDisplayData(cardType, resultData, ele);

            setCurrentGeneratedResults(prev => ({
                ...prev,
                [cardType]: displayData
            }));

            setCurrentCardStatus(prev => ({
                ...prev,
                [cardType]: 'success'
            }));
        } catch (error) {
            console.error('获取结果失败:', error);
            setCurrentCardStatus(prev => ({
                ...prev,
                [cardType]: 'error'
            }));
        }
    }, [buildDisplayData, setCurrentGeneratedResults, setCurrentCardStatus]);

    // 轮询生成状态
    const pollGenerateStatus = useCallback(async (taskId, taskType, cardType, ele, maxAttempts = 60, interval = 6000) => {
        let attempts = 0;

        const checkStatus = async () => {
            try {
                const statusResponse = await handleMultimodalGenerateStatus({
                    task_id: taskId,
                    task_type: taskType
                });
                const statusData = statusResponse?.data || statusResponse;
                const status = extractStatus(statusData, taskType);

                if (isSuccessStatus(status)) {
                    await fetchGenerateResult(taskId, taskType, cardType, ele);
                } else if (isFailedStatus(status)) {
                    throw new Error('生成失败');
                } else {
                    attempts++;
                    if (attempts < maxAttempts) {
                        setTimeout(checkStatus, interval);
                    } else {
                        throw new Error('生成超时，请稍后重试');
                    }
                }
            } catch (error) {
                console.error('轮询状态失败:', error);
                setCurrentCardStatus(prev => ({
                    ...prev,
                    [cardType]: 'error'
                }));
            }
        };

        checkStatus();
    }, [extractStatus, isSuccessStatus, isFailedStatus, fetchGenerateResult, setCurrentCardStatus]);

    // 处理卡片点击
    const handleItemClick = useCallback(async (ele, isRetry = false) => {
        const cardType = ele.type;
        const currentCardStatus = getCurrentCardStatus();
        const currentGeneratedResults = getCurrentGeneratedResults();

        if (isRetry) {
            setCurrentCardStatus(prev => ({ ...prev, [cardType]: 'loading' }));
        }

        if (currentCardStatus[cardType] === 'loading' && !isRetry) {
            return;
        }

        if (currentCardStatus[cardType] === 'success') {
            const resultData = currentGeneratedResults[cardType];
            if (resultData && (resultData.description || resultData.imageUrl || resultData.audioUrl)) {
                onOpenDetailModal?.(resultData);
            } else {
                try {
                    const taskType = getTaskType(cardType);
                    const resultResponse = await handleMultimodalGenerateResult({
                        task_id: reference_id,
                        task_type: taskType
                    });
                    const apiResultData = resultResponse?.data || resultResponse;
                    const displayData = buildDisplayData(cardType, apiResultData, ele);
                    setCurrentGeneratedResults(prev => ({ ...prev, [cardType]: displayData }));
                    onOpenDetailModal?.(displayData);
                } catch (error) {
                    console.error('获取结果失败:', error);
                    setCurrentCardStatus(prev => ({ ...prev, [cardType]: 'error' }));
                }
            }
            return;
        }

        if (!reference_id) {
            if (!isRetry) {
                ToastUtils.showToast('缺少必要的参数 reference_id');
                setCurrentCardStatus(prev => ({ ...prev, [cardType]: 'error' }));
            }
            return;
        }

        if (!isRetry) {
            setCurrentCardStatus(prev => ({ ...prev, [cardType]: 'loading' }));
        }

        try {
            const taskType = getTaskType(cardType);
            const timeoutMs = 60000;
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('生成请求超时，请稍后重试')), timeoutMs)
            );
            const response = await Promise.race([
                handleMultimodalGenerate({
                    task_id: reference_id,
                    task_type: taskType
                }),
                timeoutPromise
            ]);
            if (!response) {
                throw new Error('生成请求失败');
            }

            const responseData = response?.data || response;
            const successFlag = responseData?.success !== undefined ? responseData.success : response?.success;
            if (successFlag === false) {
                throw new Error(responseData?.msg || '生成失败');
            }

            const initialStatus = extractStatus(responseData, taskType);

            if (isSuccessStatus(initialStatus)) {
                await fetchGenerateResult(reference_id, taskType, cardType, ele);
                return;
            }

            if (isFailedStatus(initialStatus)) {
                throw new Error(responseData?.message || '生成失败');
            }

            pollGenerateStatus(reference_id, taskType, cardType, ele, 60, 6000);
        } catch (error) {
            console.error('生成失败:', error);
            setCurrentCardStatus(prev => ({ ...prev, [cardType]: 'error' }));
        }
    }, [
        getCurrentCardStatus,
        getCurrentGeneratedResults,
        setCurrentCardStatus,
        setCurrentGeneratedResults,
        reference_id,
        getTaskType,
        extractStatus,
        isSuccessStatus,
        isFailedStatus,
        fetchGenerateResult,
        pollGenerateStatus,
        buildDisplayData,
        onOpenDetailModal
    ]);

    // 重试生成
    const handleRetry = useCallback((cardType) => {
        const effectiveId = getEffectiveMessageId();
        if (effectiveId && requestedTasksRef.current[effectiveId]) {
            requestedTasksRef.current[effectiveId][cardType] = false;
        }

        const cardInfo = {
            title: cardType === 'text_summary' ? '文字总结' :
                   cardType === 'mind_map' ? '思维导图' : '播客',
            description: cardType === 'text_summary' ? '精炼核心,快速抓住重点信息' :
                        cardType === 'mind_map' ? '可视化知识结构,让思路一目了然' :
                        '听觉化知识,填充你的碎片时间',
            type: cardType
        };
        handleItemClick(cardInfo, true);
    }, [getEffectiveMessageId, handleItemClick]);

    // 初始化状态
    useEffect(() => {
        const effectiveId = getEffectiveMessageId();
        if (effectiveId && reference_id) {
            if (!cardStatusMap[effectiveId]) {
                const initialStatus = initializeCardStatusFromTaskList(task_list);
                setCardStatusForId(effectiveId, initialStatus);
            }

            if (!generatedResultsMap[effectiveId]) {
                setGeneratedResultsMap(prev => ({
                    ...prev,
                    [effectiveId]: {
                        text_summary: null,
                        mind_map: null,
                        video_player: null
                    }
                }));
            }
        }
    }, [messageId, activeDetailMessageId, detailPanelOpen, task_list, reference_id, getEffectiveMessageId, cardStatusMap, generatedResultsMap, initializeCardStatusFromTaskList, setCardStatusForId]);

    // 自动处理已完成或进行中的任务
    useEffect(() => {
        const effectiveId = getEffectiveMessageId();
        if (effectiveId && reference_id && Array.isArray(task_list) && task_list.length > 0) {
            const currentStatus = cardStatusMap[effectiveId];
            if (!currentStatus) return;

            if (!requestedTasksRef.current[effectiveId]) {
                requestedTasksRef.current[effectiveId] = {
                    text_summary: false,
                    mind_map: false,
                    video_player: false
                };
            }

            task_list.forEach((task) => {
                const taskType = Number(task.task_type);
                const taskStatus = Number(task.task_status);
                const cardTypeMap = { 1: 'text_summary', 2: 'mind_map', 3: 'video_player' };
                const cardType = cardTypeMap[taskType];
                if (!cardType) return;

                const currentCardStatus = currentStatus[cardType];
                const hasRequested = requestedTasksRef.current[effectiveId]?.[cardType];

                if (currentCardStatus === 'loading') {
                    if (taskStatus === 1 && !hasRequested) {
                        requestedTasksRef.current[effectiveId][cardType] = true;
                        setTimeout(async () => {
                            try {
                                const resultResponse = await handleMultimodalGenerateResult({
                                    task_id: reference_id,
                                    task_type: taskType
                                });
                                const apiResultData = resultResponse?.data || resultResponse;
                                const displayData = buildDisplayData(cardType, apiResultData);
                                setCurrentGeneratedResults(prev => ({ ...prev, [cardType]: displayData }));
                                setCurrentCardStatus(prev => ({ ...prev, [cardType]: 'success' }));
                            } catch (error) {
                                console.error('自动获取结果失败:', error);
                                requestedTasksRef.current[effectiveId][cardType] = false;
                                setCurrentCardStatus(prev => ({ ...prev, [cardType]: 'error' }));
                            }
                        }, 100);
                    } else if (taskStatus === 2 && !hasRequested) {
                        requestedTasksRef.current[effectiveId][cardType] = true;
                        setTimeout(() => {
                            pollGenerateStatus(reference_id, taskType, cardType, {
                                type: cardType,
                                title: cardType === 'text_summary' ? '文字总结' :
                                       cardType === 'mind_map' ? '思维导图' : '播客'
                            });
                        }, 100);
                    }
                    return;
                }

                if (taskStatus === 3 && currentCardStatus !== 'error') {
                    setCurrentCardStatus(prev => ({ ...prev, [cardType]: 'error' }));
                    return;
                }

                if (taskStatus === 1 && currentCardStatus !== 'success' && !hasRequested) {
                    requestedTasksRef.current[effectiveId][cardType] = true;
                    setTimeout(async () => {
                        try {
                            const resultResponse = await handleMultimodalGenerateResult({
                                task_id: reference_id,
                                task_type: taskType
                            });
                            const apiResultData = resultResponse?.data || resultResponse;
                            const displayData = buildDisplayData(cardType, apiResultData);
                            setCurrentGeneratedResults(prev => ({ ...prev, [cardType]: displayData }));
                            setCurrentCardStatus(prev => ({ ...prev, [cardType]: 'success' }));
                        } catch (error) {
                            console.error('自动获取结果失败:', error);
                            requestedTasksRef.current[effectiveId][cardType] = false;
                            setCurrentCardStatus(prev => ({ ...prev, [cardType]: 'error' }));
                        }
                    }, 100);
                } else if (taskStatus === 2 && currentCardStatus !== 'loading' && !hasRequested) {
                    requestedTasksRef.current[effectiveId][cardType] = true;
                    setCurrentCardStatus(prev => ({ ...prev, [cardType]: 'loading' }));
                    setTimeout(() => {
                        pollGenerateStatus(reference_id, taskType, cardType, {
                            type: cardType,
                            title: cardType === 'text_summary' ? '文字总结' :
                                   cardType === 'mind_map' ? '思维导图' : '播客'
                        });
                    }, 100);
                }
            });
        }
    }, [task_list, messageId, reference_id, cardStatusMap, getEffectiveMessageId, buildDisplayData, setCurrentGeneratedResults, setCurrentCardStatus, pollGenerateStatus]);

    return {
        getCurrentCardStatus,
        getCurrentGeneratedResults,
        handleItemClick,
        handleRetry
    };
};

export default useMultimodalCardGeneration;

